import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, MapPin, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { ReptileCard } from "../components/ReptileCard";

const BreederProfile = () => {
  const { slug } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    // Try by slug; fallback derive
    api.get(`/breeders/${slug}`).then((r) => setData(r.data)).catch(() => {
      // try by id derivative
      api.get("/breeders").then((r) => {
        const found = r.data.find((b) => b.slug === slug || b.id.endsWith(slug));
        if (found) {
          api.get(`/breeders/${found.slug}`).then((rr) => setData(rr.data));
        }
      });
    });
  }, [slug]);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-gray-500 font-mono-tech text-xs">LOADING…</div>;
  const { breeder, reptiles } = data;

  return (
    <div data-testid="breeder-profile-page" className="bg-[var(--monzal-bg)] min-h-screen">
      <div className="relative h-[40vh] lg:h-[55vh] overflow-hidden">
        <img src={breeder.cover} alt={breeder.name} className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--monzal-bg)] via-black/40 to-transparent" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 h-full flex flex-col justify-end pb-10">
          <Link to="/breeders" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-display text-gray-300 hover:text-[var(--monzal-gold)] mb-6 w-fit">
            <ArrowLeft size={14} /> {t("nav.breeders")}
          </Link>
          <div className="flex items-end gap-6 flex-wrap">
            <div className="w-20 h-20 lg:w-28 lg:h-28 border-2 border-[var(--monzal-gold)] overflow-hidden">
              <img src={breeder.avatar} alt={breeder.name} className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="overline flex items-center gap-3">
                <MapPin size={12} /> {breeder.location}
                {breeder.verified && (
                  <span className="flex items-center gap-1 text-[var(--monzal-emerald)]">
                    <ShieldCheck size={12} /> {t("breeder.verified")}
                  </span>
                )}
              </div>
              <h1 className="font-display font-light text-3xl lg:text-6xl text-white tracking-tight mt-3" data-testid="breeder-name">{breeder.name}</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="overline mb-3">BIO</div>
          <p className="text-gray-300 leading-relaxed">{breeder.bio}</p>
          <div className="mt-8">
            <div className="overline mb-3">{t("breeder.specialties")}</div>
            <div className="flex flex-wrap gap-2">
              {breeder.specialties.map((s) => <span key={s} className="tag-gold">{s}</span>)}
            </div>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div>
              <div className="font-display text-3xl text-[var(--monzal-gold)]">{breeder.years_active}</div>
              <div className="overline mt-1">{t("breeder.yearsActive")}</div>
            </div>
            <div>
              <div className="font-display text-3xl text-[var(--monzal-gold)]">{reptiles.length}</div>
              <div className="overline mt-1">{t("breeder.animals")}</div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-8">
          <div className="overline mb-6">{t("breeder.animals")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {reptiles.map((r) => <ReptileCard key={r.id} reptile={r} testIdPrefix="breeder-reptile" />)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BreederProfile;
